const AUDIO_LEVEL_FLOOR = 0.012;

/**
 * Converts one PCM frame into a normalized 0..1 level for live voice UI.
 * A small noise floor keeps an open microphone from looking active in a quiet
 * room, while the square root makes ordinary speech legible without clipping.
 */
export function pcmAudioLevel(samples: Float32Array): number {
  if (samples.length === 0) {
    return 0;
  }

  let sumOfSquares = 0;
  for (const sample of samples) {
    sumOfSquares += sample * sample;
  }

  const rms = Math.sqrt(sumOfSquares / samples.length);
  if (rms <= AUDIO_LEVEL_FLOOR) {
    return 0;
  }
  return Math.min(1, Math.sqrt((rms - AUDIO_LEVEL_FLOOR) / (1 - AUDIO_LEVEL_FLOOR)));
}

/** Keeps a bounded history of real audio samples, newest last. */
export function appendAudioLevel(
  levels: readonly number[],
  level: number,
  capacity: number,
): number[] {
  if (capacity <= 0) {
    return [];
  }

  const normalized = Number.isFinite(level) ? Math.min(1, Math.max(0, level)) : 0;
  const retainedFrom = Math.max(0, levels.length - capacity + 1);
  return [...levels.slice(retainedFrom), normalized];
}

/**
 * Projects a level history onto a fixed number of bars. Missing samples stay
 * flat rather than inventing activity, and the newest sample sits at the end.
 */
export function waveformLevels(levels: readonly number[], bars: number): number[] {
  if (bars <= 0) {
    return [];
  }

  const visible = levels.slice(-bars);
  const emptyBars = bars - visible.length;
  return [
    ...Array.from({ length: emptyBars }, () => 0),
    ...visible.map((level) => (Number.isFinite(level) ? Math.min(1, Math.max(0, level)) : 0)),
  ];
}
